/**
 * Item Relationship Actions
 *
 * Surfaces the 11 canonical relationship actions (Cluster, Package, Competing,
 * Dependency, Prerequisite, Aggregation, Breakdown, Contains, Elaboration,
 * Collection, Reframe) as "things you can do to an item."
 * Lineage lives on the item's Lineage tab — not here.
 * Per canon 07-item-relationship-taxonomy-and-mutation-rules.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  X,
  Info,
  RefreshCw,
  AlertTriangle,
  Compass,
} from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { CATEGORY_BADGE, CATEGORY_BADGE_FALLBACK } from '../../../lib/itemCategoryColors';
import { useTheme } from '../../../contexts/ThemeContext';
import type { Candidate } from '../../../types/prioritization/lifecycle';
import { getElaborationFrameScope } from '../../../types/prioritization/itemRelationships';
import { RelationshipReferenceModal, type RelationshipReferenceTab } from '../relationships/RelationshipReferenceModal';
import { normalizeSequencePair } from '../../../lib/sequenceRelationships';
import {
  getRelationshipGuideEntry,
  relationshipPillClass,
} from '../../../lib/relationshipGuide';

// ============================================================================
// Taxonomy config — canon 07
// ============================================================================

type TaxonomyEntry = {
  id: string;
  label: string;
  bond: string;
  axis: 'horizontal' | 'vertical' | 'lateral' | 'temporal' | 'framing';
};

const TAXONOMY: TaxonomyEntry[] = [
  { id: 'cluster',      label: 'Cluster',      bond: 'Identity',            axis: 'horizontal' },
  { id: 'package',      label: 'Package',      bond: 'Atomicity',           axis: 'horizontal' },
  { id: 'exclusive',    label: 'Competing',    bond: 'One or the other, not both', axis: 'horizontal' },
  { id: 'sequence',     label: 'Sequence & conditions', bond: 'Before this · Waiting on this', axis: 'horizontal' },
  { id: 'composition',  label: 'Composition',  bond: 'Whole and parts',     axis: 'vertical' },
  { id: 'contains',     label: 'Contains',     bond: 'Reporting hierarchy', axis: 'vertical' },
  {
    id: 'elaboration',
    label: getRelationshipGuideEntry('elaboration')?.label ?? 'Elaboration',
    bond: getRelationshipGuideEntry('elaboration')?.bond ?? 'Possibility spectrum',
    axis: 'vertical',
  },
  { id: 'collection',   label: 'Collection',   bond: 'Curation',            axis: 'lateral' },
  { id: 'reframe',      label: 'Additional Category', bond: 'Perspective',         axis: 'framing' },
];


// ============================================================================
// Types
// ============================================================================

interface ItemRelationshipActionsProps {
  item: Candidate;
  readOnly?: boolean;
  onDiscoverClick?: () => void;
  onClusterClick?: () => void;
  onPackageClick?: () => void;
  onExclusiveClick?: () => void;
  onDependencyClick?: () => void;
  onPrerequisiteClick?: () => void;
  onAggregationClick?: () => void;
  onBreakdownClick?: () => void;
  onContainsClick?: () => void;
  onElaborationClick?: () => void;
  onCollectionClick?: () => void;
  onReframeClick?: () => void;
}

type Pill = { id: string; label: string; href?: string; onRemove?: () => void; colorClass?: string };

// ============================================================================
// Component
// ============================================================================

export function ItemRelationshipActions({
  item,
  readOnly = false,
  onDiscoverClick,
  onClusterClick,
  onPackageClick,
  onExclusiveClick,
  onDependencyClick,
  onPrerequisiteClick,
  onAggregationClick,
  onBreakdownClick,
  onContainsClick,
  onElaborationClick,
  onCollectionClick,
  onReframeClick,
}: ItemRelationshipActionsProps) {
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allGroupMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);
  const getItemById = useItemsStore((s) => s.getItemById);
  const { isDark } = useTheme();

  // Frames for the Reframe row — read from store so they stay live
  const storeItem = useItemsStore((s) => s.items.find((i) => i.id === item.id));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemFrames = useMemo(() => storeItem?.frames ?? (item as any).frames ?? [], [storeItem?.frames, item]);
  const itemCategory = storeItem?.itemCategory ?? item.itemCategory;
  const canonicalFrameId = storeItem?.canonicalFrameId;

  // Collections
  const allCollections = useCollectionsStore((s) => s.collections);
  const collectionMemberships = useCollectionsStore((s) => s.memberships);
  const removeItemFromCollection = useCollectionsStore((s) => s.removeItemFromCollection);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  useEffect(() => { void fetchCollections(); }, [fetchCollections]);

  const [showGuide, setShowGuide] = useState(false);
  const [guideTab, setGuideTab] = useState<RelationshipReferenceTab>('types');

  const itemCollections = useMemo(() => {
    const ids = new Set(
      collectionMemberships.filter((m) => m.itemId === item.id).map((m) => m.collectionId),
    );
    return allCollections.filter((c) => ids.has(c.id));
  }, [allCollections, collectionMemberships, item.id]);

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const groups = useMemo(() => {
    const memberGroupIds = new Set(
      allGroupMemberships.filter((m) => m.itemId === item.id).map((m) => m.groupId),
    );
    return allGroups.filter((g) => memberGroupIds.has(g.id));
  }, [allGroups, allGroupMemberships, item.id]);

  const edges = useMemo(
    () => allEdges.filter((e) => e.sourceItemId === item.id || e.targetItemId === item.id),
    [allEdges, item.id],
  );

  const clusterGroups = groups.filter((g) => g.type === 'cluster' && g.status !== 'dissolved');
  const packageGroups = groups.filter((g) => g.type === 'package' && g.status !== 'dissolved');
  const exclusiveGroups = groups.filter((g) => g.type === 'exclusive' && g.status !== 'dissolved');
  const compositionEdges = useMemo(() => {
    const byPair = new Map<string, typeof edges[number]>();
    for (const edge of edges) {
      if ((edge.type !== 'aggregation' && edge.type !== 'breakdown') || edge.status === 'dissolved') continue;
      const key = `${edge.sourceItemId}:${edge.targetItemId}`;
      if (!byPair.has(key)) byPair.set(key, edge);
    }
    return [...byPair.values()];
  }, [edges]);
  const containsEdgesRaw = edges.filter((e) => e.type === 'contains' && e.status !== 'dissolved');
  const elaborationEdges = edges.filter((e) => e.type === 'elaboration' && e.status !== 'dissolved');

  const containsParentEdges = containsEdgesRaw.filter((e) => e.sourceItemId === item.id);
  const containsChildEdges = containsEdgesRaw.filter((e) => e.targetItemId === item.id);
  const sequencePairs = useMemo(
    () => {
      const byPair = new Map<string, ReturnType<typeof normalizeSequencePair>>();
      for (const edge of allEdges) {
        const pair = normalizeSequencePair(edge);
        if (!pair) continue;
        const key = `${pair.predecessorItemId}:${pair.dependentItemId}`;
        if (!byPair.has(key)) byPair.set(key, pair);
      }
      return [...byPair.values()].filter((pair): pair is NonNullable<typeof pair> => pair != null);
    },
    [allEdges],
  );

  // Build reframe pills: the item's own category (lead) + each alternate frame.
  // Each pill uses the canonical category color for its frame type.
  const reframePills = useMemo(() => {
    const pills: Pill[] = [];

    const pillColorFor = (cat: string): string => {
      const b = CATEGORY_BADGE[cat.toLowerCase()] ?? CATEGORY_BADGE_FALLBACK;
      return `${b.border} ${b.bg} ${b.text} hover:opacity-80`;
    };

    if (itemCategory) {
      const typeLabel = itemCategory.charAt(0).toUpperCase() + itemCategory.slice(1);
      pills.push({ id: '__own__', label: `${typeLabel} (lead)`, colorClass: pillColorFor(itemCategory) });
    }
    for (const frame of itemFrames) {
      const ft: string | undefined = frame.frameType ?? (frame as { frame_type?: string }).frame_type;
      if (!ft || ft === itemCategory) continue;
      const typeLabel = ft.charAt(0).toUpperCase() + ft.slice(1);
      const isCanonical = frame.id === canonicalFrameId;
      pills.push({
        id: frame.id,
        label: isCanonical ? `${typeLabel} (active)` : typeLabel,
        colorClass: pillColorFor(ft),
      });
    }
    return pills;
  }, [itemFrames, itemCategory, canonicalFrameId]);

  const pillsByType: Record<string, Array<{ id: string; label: string; href?: string; onRemove?: () => void }>> = {
    cluster: clusterGroups.map((g) => ({
      id: g.id,
      label: g.title || 'Unnamed cluster',
      href: `/prioritization/relationships/clusters/${g.id}`,
    })),
    package: packageGroups.map((g) => ({
      id: g.id,
      label: g.title || 'Unnamed package',
      href: `/prioritization/relationships/packages/${g.id}`,
    })),
    exclusive: exclusiveGroups.map((g) => ({
      id: g.id,
      label: g.title || 'Unnamed competing set',
      href: `/prioritization/relationships/exclusives/${g.id}`,
    })),
    sequence: sequencePairs
      .filter((pair) => pair.predecessorItemId === item.id || pair.dependentItemId === item.id)
      .map((pair) => {
        const isDependent = pair.dependentItemId === item.id;
        const otherId = isDependent ? pair.predecessorItemId : pair.dependentItemId;
        const other = getItemById(otherId);
        return {
          id: pair.edgeId,
          label: `${isDependent ? 'Before this' : 'Waiting on this'}: ${other?.title || 'Related item'}`,
          href: `/prioritization/items/${otherId}`,
        };
      }),
    composition: compositionEdges.map((edge) => {
      const isWhole = edge.sourceItemId === item.id;
      const otherId = isWhole ? edge.targetItemId : edge.sourceItemId;
      const other = getItemById(otherId);
      return {
        id: edge.id,
        label: `${isWhole ? 'Part of this' : 'This is part of'}: ${other?.title || 'Related item'}`,
        href: `/prioritization/items/${otherId}`,
      };
    }),
    contains: [
      ...containsParentEdges.map((e) => {
        const other = getItemById(e.targetItemId);
        return { id: e.id, label: `${other?.title || 'Related item'} (child)`, href: `/prioritization/items/${e.targetItemId}` };
      }),
      ...containsChildEdges.map((e) => {
        const other = getItemById(e.sourceItemId);
        return { id: `inv-${e.id}`, label: `${other?.title || 'Related item'} (parent)`, href: `/prioritization/items/${e.sourceItemId}` };
      }),
    ],
    elaboration: elaborationEdges.map((e) => {
      if (e.sourceItemId === item.id) {
        const other = getItemById(e.targetItemId);
        const scope = getElaborationFrameScope(e);
        const scopeNote = scope.length > 0 ? ` · ${scope.join('/')}` : '';
        return { id: e.id, label: `${other?.title || 'Related item'} (alternative${scopeNote})`, href: `/prioritization/items/${e.targetItemId}` };
      } else {
        const other = getItemById(e.sourceItemId);
        return { id: `inv-${e.id}`, label: `${other?.title || 'Related item'} (parent need)`, href: `/prioritization/items/${e.sourceItemId}` };
      }
    }),
    collection: itemCollections.map((c) => ({
      id: c.id,
      label: c.name || 'Unnamed collection',
      href: `/prioritization/collections/${c.id}`,
      onRemove: readOnly ? undefined : () => void removeItemFromCollection(c.id, item.id),
    })),
    reframe: reframePills,
  };

  const axisOrder: Array<'horizontal' | 'vertical' | 'lateral' | 'framing'> = [
    'horizontal',
    'vertical',
    'lateral',
    'framing',
  ];

  const byAxis = useMemo(() => {
    const map: Record<string, TaxonomyEntry[]> = { horizontal: [], vertical: [], lateral: [], framing: [] };
    for (const t of TAXONOMY) {
      map[t.axis].push(t);
    }
    return map;
  }, []);

  return (
    <>
    <div className={` border p-6 ${isDark ? 'border-[var(--slate)]/30 bg-[var(--slate)]/40' : 'border-[var(--rule)] bg-[var(--bg-raised)]'}`}>
      <div className="flex items-start justify-between mb-1">
        <h2 className={`text-lg font-semibold ${isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>Item Relationship Inspector</h2>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          {onDiscoverClick && (
            <button
              type="button"
              onClick={onDiscoverClick}
              title="Discover relationships — guided walkthrough"
              className={`p-1 rounded-full transition-colors ${isDark ? 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate)]/10' : 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)]'}`}
            >
              <Compass className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => { setGuideTab('types'); setShowGuide(true); }}
            title="Relationship types guide"
            className={`p-1 rounded-full transition-colors ${isDark ? 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate)]/10' : 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)]'}`}
          >
            <Info className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => { setGuideTab('patterns'); setShowGuide(true); }}
            title="Failure patterns catalog"
            className={`p-1 rounded-full transition-colors ${isDark ? 'text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard)]/10' : 'text-[var(--text-ter)] hover:text-[var(--mustard)] hover:bg-[var(--mustard-dim)]'}`}
          >
            <AlertTriangle className="h-4 w-4" />
          </button>
        </div>
      </div>
      <p className={`text-sm mb-4 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
        Relationships, categories, and protections for this item
      </p>
      <div className="space-y-4">
        {axisOrder.map((axis) => {
          const entries = byAxis[axis];
          if (!entries || entries.length === 0) return null;
          const axisLabel = axis === 'horizontal' ? 'Horizontal' : axis === 'vertical' ? 'Vertical' : axis === 'lateral' ? 'Curatorial' : 'Category';
          return (
            <div key={axis}>
              <h3 className={`text-xs font-medium uppercase tracking-wide mb-2 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{axisLabel}</h3>
              <div className="space-y-2">
                {entries.map((t) => (
                  <TaxonomyRow
                    key={t.id}
                    entry={t}
                    pills={pillsByType[t.id] ?? []}
                    readOnly={readOnly}
                    itemId={item.id}
                    onClusterClick={onClusterClick}
                    onPackageClick={onPackageClick}
                    onExclusiveClick={onExclusiveClick}
                    onDependencyClick={onDependencyClick}
                    onPrerequisiteClick={onPrerequisiteClick}
                    onAggregationClick={onAggregationClick}
                    onBreakdownClick={onBreakdownClick}
                    onContainsClick={onContainsClick}
                    onElaborationClick={onElaborationClick}
                    onCollectionClick={onCollectionClick}
                    onReframeClick={onReframeClick}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    <RelationshipReferenceModal
      isOpen={showGuide}
      onClose={() => setShowGuide(false)}
      isDark={isDark}
      initialTab={guideTab}
    />
    </>
  );
}

// ============================================================================
// Taxonomy row
// ============================================================================

function TaxonomyRow({
  entry,
  pills,
  readOnly,
  onClusterClick,
  onPackageClick,
  onExclusiveClick,
  onDependencyClick,
  onPrerequisiteClick,
  onAggregationClick,
  onBreakdownClick,
  onContainsClick,
  onElaborationClick,
  onCollectionClick,
  onReframeClick,
}: {
  entry: TaxonomyEntry;
  pills: Pill[];
  readOnly: boolean;
  itemId: string;
  onClusterClick?: () => void;
  onPackageClick?: () => void;
  onExclusiveClick?: () => void;
  onDependencyClick?: () => void;
  onPrerequisiteClick?: () => void;
  onAggregationClick?: () => void;
  onBreakdownClick?: () => void;
  onContainsClick?: () => void;
  onElaborationClick?: () => void;
  onCollectionClick?: () => void;
  onReframeClick?: () => void;
})  {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const canAct = !readOnly;
  const guide = getRelationshipGuideEntry(entry.id);
  const GuideIcon = guide?.Icon;

  const handleAddAction = () => {
    if (entry.id === 'cluster') onClusterClick?.();
    else if (entry.id === 'package') onPackageClick?.();
    else if (entry.id === 'exclusive') onExclusiveClick?.();
    else if (entry.id === 'sequence') {
      if (onPrerequisiteClick) onPrerequisiteClick();
      else onDependencyClick?.();
    } else if (entry.id === 'composition') {
      if (onAggregationClick) onAggregationClick();
      else onBreakdownClick?.();
    } else if (entry.id === 'contains') onContainsClick?.();
    else if (entry.id === 'elaboration') onElaborationClick?.();
    else if (entry.id === 'collection') onCollectionClick?.();
    else if (entry.id === 'reframe') onReframeClick?.();
  };

  const showAddButton = canAct;

  return (
    <div className={`py-2 px-3  border transition-colors ${isDark ? 'border-white/8 bg-[var(--bg-raised)]/5 hover:bg-[var(--bg-raised)]/10' : 'border-[var(--rule)] bg-[var(--surface)]/50 hover:bg-[var(--surface)]'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {GuideIcon && (
            <GuideIcon className={`h-3.5 w-3.5 shrink-0 ${isDark ? guide?.iconColorDark : guide?.iconColor}`} />
          )}
          <div className="min-w-0">
            <span className={`text-sm font-medium ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>{entry.label}</span>
            <span className={`text-xs ml-2 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>— {entry.bond}</span>
          </div>
        </div>
        {showAddButton && (
          <button
            type="button"
            onClick={handleAddAction}
            title={entry.id === 'reframe' ? 'Add an additional category' : undefined}
            className="text-sm font-medium text-primary-600 hover:text-primary-800 inline-flex items-center gap-1 shrink-0 ml-3"
          >
            {entry.id === 'reframe' ? <RefreshCw className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            Add
          </button>
        )}
      </div>
      {pills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {pills.map((pill) => (
            <span
              key={pill.id}
              className={`inline-flex items-center gap-1 max-w-[18rem] pl-2.5 ${pill.onRemove ? 'pr-1' : 'pr-2.5'} py-0.5 rounded-full text-xs font-medium border transition-colors
                ${pill.colorClass ?? relationshipPillClass(entry.id, isDark)}`}
            >
              <button
                type="button"
                onClick={() => pill.href && navigate(pill.href)}
                className={`truncate ${pill.href ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
                title={pill.label}
              >
                {pill.label}
              </button>
              {pill.onRemove && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); pill.onRemove?.(); }}
                  className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className={`text-xs mt-0.5 ${isDark ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'}`}>None</p>
      )}
    </div>
  );
}
